import { useTranslation } from "react-i18next";
import { Grid, GridColumn } from "semantic-ui-react";

import { BrandGroupSelect } from "components";
import { createFormHelper } from "lib/Form";
import { DayplannerStatuses } from "modules/AccountSettings/components";
import { SELECT_ALL_CAR_TYPES, useVehicleTypeDropdownOptions } from "modules/AccountSettings/hooks/useVehicleTypeDropdownOptions";

import type { User } from "models";
import type { DayplannerStatusDataType } from "modules/AccountSettings/components";

type WorkshopDayOverviewSectionProps = {
  dayplannerStatusesFilter: number[];
  handleDayplannerStatusChange: (data: DayplannerStatusDataType[]) => void;
};

const FormHelper =
  createFormHelper<
    Pick<
      User,
      | "car_type_filter"
      | "dayplanner_brand_group_ids"
      | "day_planner_appointments_collapsed_by_default"
      | "day_planner_cars_ready_collapsed_by_default"
      | "day_planner_quality_control_collapsed_by_default"
    >
  >();

export const WorkshopDayOverviewSection = ({ handleDayplannerStatusChange, dayplannerStatusesFilter }: WorkshopDayOverviewSectionProps) => {
  const { t } = useTranslation();
  const { watch } = FormHelper.useFormContext();
  const selectedCarTypeFilter = watch("car_type_filter");

  const vehicleTypeOptions = useVehicleTypeDropdownOptions();
  const selectedVehicleTypeOption = vehicleTypeOptions.find(op => op.value === (selectedCarTypeFilter ?? SELECT_ALL_CAR_TYPES));

  return (
    <div className="setting_section">
      <h2>{t("v8_workshop_day_overview", "Workshop Day Overview")}</h2>

      <label className="input-label">{t("v8_collapse_columns_by_default", "Collapse columns by default")}</label>
      <Grid columns={9}>
        <GridColumn width={3}>
          <FormHelper.Field.Checkbox name="day_planner_appointments_collapsed_by_default" label={t("v8_appointments", "Appointments")} />
        </GridColumn>

        <GridColumn width={3}>
          <FormHelper.Field.Checkbox name="day_planner_cars_ready_collapsed_by_default" label={t("v8_cars_ready", "Cars ready")} />
        </GridColumn>

        <GridColumn width={3}>
          <FormHelper.Field.Checkbox name="day_planner_quality_control_collapsed_by_default" label={t("v8_quality_control", "Quality Control")} />
        </GridColumn>
      </Grid>

      <Grid columns={16}>
        <GridColumn width={5}>
          <div>
            <label className="input-label">{t("v8_default_statuses", "Default statuses")}</label>
            <DayplannerStatuses onSelect={handleDayplannerStatusChange} statuses={dayplannerStatusesFilter} />
          </div>
        </GridColumn>
        <GridColumn width={5}>
          <label className="input-label">{t("v8_default_brands", "Default brands")}</label>
          <FormHelper.Controller name="dayplanner_brand_group_ids" render={({ field }) => <BrandGroupSelect value={field.value ?? []} onChange={field.onChange} />} />
        </GridColumn>
        <GridColumn width={6}>
          <label className="input-label">{t("v8_vehicle_type", "Vehicle type")}</label>
          <FormHelper.Field.Dropdown
            name="car_type_filter"
            options={vehicleTypeOptions}
            parseOptionValueToFieldValue={value => (value && value > SELECT_ALL_CAR_TYPES ? value : null)}
            dropdownProps={{
              fluid: true,
              placeholder: t("v8_select_vehicle_type", { defaultValue: "Select vehicle type" }),
              trigger: selectedVehicleTypeOption?.content
            }}
          />
        </GridColumn>
      </Grid>
    </div>
  );
};
